import { useState } from "react";

import { ItemActionArea } from "@/components/ui/item-action-area";
import { ItemEmptyImageBox } from "@/components/ui/item-image-box";
import { ItemImagePreview } from "@/components/ui/item-image-preview";
import { ITEM_PLACEHOLDER_ALT } from "@/lib/catalog-item-image";
import type { CatalogItemImageInput } from "@/lib/catalog-item-image";
import type { CatalogDetailBadgeInput } from "@/lib/catalog-item-badges";
import { ItemEiLogo } from "./item-badges";
import { ItemImageLightbox } from "./item-image-lightbox";

// The details page's image column: which of the three arms an item gets, and the
// lightbox the first of them opens.
//
// The donor's `Lightbox/Images.tsx` — **feature-local** to `ItemDetailsIsland`,
// which is why this is here and not beside the gallery in `components/ui/`. The
// gallery and the two empty boxes are shared in the source and ported as shared
// components; the *choice* between them is per-screen, exactly as
// `item-image.tsx` is the cards' choice between the same boxes.
//
// The three arms, and the donor line for each:
//
//   1. **The gallery** — `ImagePreview`'s picture branch, for an item with
//      images (`ImagePreview.tsx:13,30`).
//   2. **The house illustration** on a tinted 350px panel, for an item with none
//      (`ImagePreview.tsx:46-51`; `ItemEmptyImageBox.tsx:16-33`).
//   3. **"Missing Image"** in place of the house, for an item with none that is
//      still awaiting approval (`ImagePreview.tsx:12`;
//      `ItemEmptyImageBox.tsx:35-49`).
//
// A fourth outcome is *not* a donor arm: a picture whose **fetch fails**. The
// donor gallery has no `onError` at all — its sibling `ItemCard` does
// (`ItemCard.tsx:265`) — so this is spec-added, carried onto this surface by
// #303's acceptance criteria and #269's implementation decision that the
// placeholder is "the no-image and fetch-error fallback". The donor gets away
// without one because its image host answers a missing object with a decodable
// placeholder PNG under a 403, so its `<img>` loads (#496).
//
// **A failure is per slot.** A card has one picture, so #269 story 3 can send
// the whole card to the placeholder; a gallery has up to six, and sending all of
// them there because one URL 404s would take the five working pictures — and the
// strip that reaches them — off the screen with it. So the placeholder stands in
// the slot that failed and the rest of the gallery carries on.
//
// A dead image is **not** dropped from the set, either: the donor has no
// `onError` and so never renumbers, and dropping one would shift every later
// cell's `Thumbnail {n}` down and change the count the lightbox names. What the
// spec adds is the placeholder in place of the broken glyph, and nothing else.
//
// **One donor branch is not here, because it belongs upstream.**
// `Images.tsx:26-29` renders a lone hero URL when the item's `images` list is
// empty but its `heroImgUrl` is set — a state falcon could reach because it
// derived the two fields separately. Against a current Atlas service it stays
// unstateable: the endpoint builds both from one list
// (`server/app/api/v1/endpoints/internal/catalog.py:386,396` —
// `heroImage=detail.images[0] if detail.images else None`), so empty images means
// no hero. It survives only as graceful degradation against a service that
// predates `variantImages` (#413), where a chip's summary hero is the one picture
// the page has for that variant — so the branch lives at the call site that knows
// which item is on screen, `displayedImages` in `lib/catalog-item-variants.ts`,
// and this component only ever receives a settled set.

interface ItemGalleryProps {
  /** The item's images, hero first — empty for an item with none. */
  images: readonly CatalogItemImageInput[];
  /**
   * The item's name. The gallery's `alt` verbatim, so it is the bare name and
   * not the card's `Image of {name}` — see `ItemImagePreview`.
   */
  itemName: string | null | undefined;
  /**
   * Whether the item has been approved.
   *
   * Drives arm 3. The donor compares `is_approved === false` strictly
   * (`ImagePreview.tsx:12`), so an `undefined` approval does *not* read as
   * pending there; here the contract makes it a required boolean, so that edge
   * cannot arise.
   */
  isApproved: boolean;
  /**
   * Whether the main slot opens the image at lightbox size.
   *
   * **The authed details page's addition, not the donor's gallery.** Modsy shows
   * an anonymous visitor the very same `Images` block — `ItemDetailsIsland
   * .tsx:151` is one of the few children with no `isPublic` gate on it — and
   * modsy has no lightbox on either surface. Atlas's dialog came in on spec #269
   * (stories 7-8) for the authed page; spec #270 lists the thumbnail swap among
   * the behaviours the public page preserves as-is, "a static swap, not a
   * carousel or lightbox". So the anonymous product page passes `false`.
   *
   * Defaulted to `true` because this component is the *details page's* chooser
   * and the dialog is now part of what that page is; the one surface that turns
   * it off says so at its call site, where the reason can be read.
   */
  lightbox?: boolean;
}

export function ItemGallery({ images, itemName, isApproved, lightbox = true }: ItemGalleryProps) {
  // Which image the lightbox is open on, or `null` for closed.
  const [openAt, setOpenAt] = useState<number | null>(null);

  // Fetch failures are **not** held here. Each slot owns its own — see
  // `useItemImageFailure`: the same image is drawn at three sizes on this
  // screen, those resolve to genuinely different bytes, and one verdict shared
  // between them let a missing thumbnail rung blank a main picture that had
  // loaded.

  if (images.length === 0) {
    return (
      // `ImagePreview.tsx:28-53` — the placeholder sits *inside* both the `mb: 3`
      // Box and the `CardActionArea`, so it carries the 24px below it **and**
      // the action area's hover, focus and ripple. Activating it does nothing:
      // the donor's `onClick` is on the inner `CardMedia` (`:44`), which this
      // branch does not render. A dead affordance, faithfully — and on #303's
      // decision round rather than quietly dropped.
      //
      // The picture arm's 24px is not here but between its own main image and
      // the strip, which is where the donor's Box actually puts it
      // (`ItemImagePreview`).
      <ItemActionArea className="mb-6">
        <ItemEmptyImageBox
          isPendingApproval={!isApproved}
          // `ItemEmptyImageBox.tsx:50` — the box's own literal, not the item's
          // name. The card's `Image of {name}` belongs to the other donor arm,
          // where the illustration travels as an ordinary hero URL.
          alt={ITEM_PLACEHOLDER_ALT}
          // `ImagePreview.tsx:47-50` — the donor's explicit geometry for this
          // slot: the column's full width at 350px, with the `lightgrey`
          // hairline.
          heightPx={350}
          bordered="light"
          // `ItemEmptyImageBox.tsx:17` — the donor's own `cursor: default`,
          // which is what keeps this slot reading as inert while the area around
          // it still lights up.
          className="cursor-default"
        />
      </ItemActionArea>
    );
  }

  return (
    <>
      <ItemImagePreview
        images={images}
        itemName={itemName}
        // No handler at all rather than a handler that does nothing: the absence
        // is what takes the lightbox's accessible name off the button with it.
        onOpenLightbox={lightbox ? setOpenAt : undefined}
        isApproved={isApproved}
      />
      {lightbox && (
        <ItemImageLightbox
          images={images}
          itemName={itemName}
          isApproved={isApproved}
          openAt={openAt}
          onOpenChange={(open) => {
            if (!open) setOpenAt(null);
          }}
        />
      )}
    </>
  );
}

interface ItemDetailImagesProps {
  /**
   * The item, or `null` while the read is in flight or after it failed.
   *
   * `Lightbox/Images.tsx:22-24` — `if (!item) return null`. The column stays
   * empty rather than standing a 350px placeholder panel there, which would
   * flash something on every navigation that modsy never shows.
   *
   * Typed at the badge input rather than at either item type, because that is
   * genuinely all this reads: a name, an approval, and the EI flag. Both the
   * authed `CatalogDetailItem` and the anonymous `PublicCatalogItemView`
   * satisfy it, and neither has to be named here.
   */
  item: (CatalogDetailBadgeInput & { name: string | null }) | null;
  /** The item's images, hero first — empty for an item with none. */
  images: readonly CatalogItemImageInput[];
  /** Passed through — see `ItemGallery`'s own `lightbox`. */
  lightbox?: boolean;
}

/**
 * The whole image column: the gallery, and the EI mark anchored to it.
 *
 * The rest of `Lightbox/Images.tsx` — its `position: relative` Box and the
 * `EITiersLogo` inside it (`:41-45`) — which is why it sits beside `ItemGallery`
 * rather than in the layout module: one donor file, one Atlas file.
 *
 * **An inner block, not the grid cell.** The donor's box is an auto-height
 * `<Box>` around `ImagePreview` alone, so its bottom is the gallery's bottom.
 * The grid cell above it is a `stretch` item that grows to the metadata column,
 * and anchoring there put the mark 80px off *that* — measured at 479px below the
 * picture on a real item.
 *
 * **The mark is not gated on the audience.** `ItemDetailsIsland.tsx:151` renders
 * `Images` with no `isPublic` prop at all, so an anonymous visitor sees the EI
 * mark over the picture — it is the tier *chips* in the header that modsy hides
 * from them (`Badges.tsx:21`).
 */
export function ItemDetailImages({ item, images, lightbox }: ItemDetailImagesProps) {
  if (!item) return null;

  return (
    <div className="relative">
      <ItemGallery
        images={images}
        itemName={item.name}
        isApproved={item.isApproved}
        lightbox={lightbox}
      />
      {/* `Lightbox/Images.tsx:43-45` — `bottom: 80px` from the bottom of the
        whole gallery, main image and strip together, so the mark rides up a row
        as the image count grows. */}
      <ItemEiLogo item={item} placement="detail" />
    </div>
  );
}
